import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import "./Grade7EngineeringLesson.css";
import { Canvas } from "@react-three/fiber";
import { OrbitControls } from "@react-three/drei";

/** Shuffle helper */
function shuffleArray(arr) {
  const cloned = [...arr];
  for (let i = cloned.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [cloned[i], cloned[j]] = [cloned[j], cloned[i]];
  }
  return cloned;
}

/**
 * A 3D Demo for Grade 7:
 *  - Cycles among "Suspension Bridge," "Rocket," "Electric Car"
 *  - Illustrates advanced engineering feats at Grade 7 level.
 */
function Grade7Engineering3DDemo() {
  const items = [
    {
      name: "Suspension Bridge",
      description: "Long bridges with cables supporting the deck across wide spans!",
      fact: "Engineers distribute tension forces along the cables to keep the deck stable.",
    },
    {
      name: "Rocket",
      description: "Rockets launch payloads or humans into space!",
      fact: "Engineers design powerful engines and manage weight for successful orbit.",
    },
    {
      name: "Electric Car",
      description: "Cars powered by rechargeable batteries for cleaner transportation!",
      fact: "Engineers optimize battery efficiency and regenerative braking systems.",
    },
  ];

  const [index, setIndex] = useState(0);

  const nextItem = () => {
    setIndex((prev) => (prev + 1) % items.length);
  };

  return (
    <div className="g7eng-3d-demo-container">
      <h4>3D Demo: {items[index].name}</h4>
      <p className="g7eng-demo-description">{items[index].description}</p>
      <p className="g7eng-demo-fact">
        <strong>Fun Fact:</strong> {items[index].fact}
      </p>
      <button className="g7eng-3d-next-btn" onClick={nextItem}>
        See Another Example
      </button>

      <div style={{ width: "300px", height: "200px", margin: "10px auto", background: "#f0f0f0" }}>
        <Canvas camera={{ position: [0, 2, 5], fov: 50 }}>
          <OrbitControls enablePan={false} />
          <ambientLight intensity={0.5} />
          <directionalLight position={[5, 5, 5]} />

          {/* Ground plane */}
          <mesh rotation={[-Math.PI / 2, 0, 0]} position={[0, -1, 0]}>
            <planeGeometry args={[10, 10]} />
            <meshStandardMaterial color="#ddd" />
          </mesh>

          {/* Switch shape by name */}
          <Grade7EngineeringShape name={items[index].name} />
        </Canvas>
      </div>
    </div>
  );
}

/**
 * Grade7EngineeringShape:
 * Renders a simplified 3D model for “Suspension Bridge,” “Rocket,” or “Electric Car.”
 */
function Grade7EngineeringShape({ name }) {
  if (name === "Suspension Bridge") {
    return (
      <group>
        {/* Two towers as tall boxes */}
        <mesh position={[-1.5, 0, 0]}>
          <boxGeometry args={[0.3, 2, 0.3]} />
          <meshStandardMaterial color="#757575" />
        </mesh>
        <mesh position={[1.5, 0, 0]}>
          <boxGeometry args={[0.3, 2, 0.3]} />
          <meshStandardMaterial color="#757575" />
        </mesh>
        {/* Deck as a rectangle between towers */}
        <mesh position={[0, -0.5, 0]}>
          <boxGeometry args={[3.2, 0.1, 0.6]} />
          <meshStandardMaterial color="#bdbdbd" />
        </mesh>
        {/* Cables as thin cylinders from top of towers to deck corners */}
        <mesh position={[-1.5, 1, 0.3]}>
          <cylinderGeometry args={[0.01, 0.01, 1.0, 8]} />
          <meshStandardMaterial color="#8d8d8d" />
          {/* We'll rotate or position cables, but let's keep it simple */}
        </mesh>
        <mesh position={[-1.5, 1, -0.3]}>
          <cylinderGeometry args={[0.01, 0.01, 1.0, 8]} />
          <meshStandardMaterial color="#8d8d8d" />
        </mesh>
        <mesh position={[1.5, 1, 0.3]}>
          <cylinderGeometry args={[0.01, 0.01, 1.0, 8]} />
          <meshStandardMaterial color="#8d8d8d" />
        </mesh>
        <mesh position={[1.5, 1, -0.3]}>
          <cylinderGeometry args={[0.01, 0.01, 1.0, 8]} />
          <meshStandardMaterial color="#8d8d8d" />
        </mesh>
      </group>
    );
  } else if (name === "Rocket") {
    return (
      <group>
        {/* Rocket body as a cylinder */}
        <mesh>
          <cylinderGeometry args={[0.1, 0.1, 2, 16]} />
          <meshStandardMaterial color="#ff5722" />
        </mesh>
        {/* Nose cone */}
        <mesh position={[0, 1.0, 0]}>
          <coneGeometry args={[0.1, 0.3, 16]} />
          <meshStandardMaterial color="#d84315" />
        </mesh>
        {/* Fins at bottom */}
        <mesh position={[0, -1, 0]} rotation={[0, 0, 0]}>
          <boxGeometry args={[0.02, 0.3, 0.4]} />
          <meshStandardMaterial color="#bf360c" />
        </mesh>
        <mesh position={[0, -1, 0]} rotation={[0, Math.PI / 2, 0]}>
          <boxGeometry args={[0.02, 0.3, 0.4]} />
          <meshStandardMaterial color="#bf360c" />
        </mesh>
      </group>
    );
  } else if (name === "Electric Car") {
    return (
      <group>
        {/* Car body as a box */}
        <mesh>
          <boxGeometry args={[1.5, 0.5, 0.8]} />
          <meshStandardMaterial color="#4caf50" />
        </mesh>
        {/* Wheels as spheres */}
        <mesh position={[-0.5, -0.3, 0.5]}>
          <sphereGeometry args={[0.2, 16, 16]} />
          <meshStandardMaterial color="black" />
        </mesh>
        <mesh position={[0.5, -0.3, 0.5]}>
          <sphereGeometry args={[0.2, 16, 16]} />
          <meshStandardMaterial color="black" />
        </mesh>
        <mesh position={[-0.5, -0.3, -0.5]}>
          <sphereGeometry args={[0.2, 16, 16]} />
          <meshStandardMaterial color="black" />
        </mesh>
        <mesh position={[0.5, -0.3, -0.5]}>
          <sphereGeometry args={[0.2, 16, 16]} />
          <meshStandardMaterial color="black" />
        </mesh>
      </group>
    );
  }
  return null;
}

/** Additional small challenge or example for Grade 7 */
function Grade7AdvancedChallenge() {
  const examples = [
    {
      name: "Design a Model Skyscraper",
      description: "Test wind loads and see how to stabilize the tall structure.",
      tip: "Use cross-bracing or tuned mass dampers to reduce sway!",
    },
    {
      name: "Plan an Electric Train System",
      description: "Figure out power supply, station layout, and efficiency for a city.",
      tip: "Regenerative braking can save energy, lowering total usage!",
    },
    {
      name: "Build a Small Hydro Turbine",
      description: "Harness flowing water to spin a turbine and generate electricity.",
      tip: "Use a water wheel or propeller design for maximum flow capture.",
    },
  ];
  const [index, setIndex] = useState(0);
  const next = () => setIndex((prev) => (prev + 1) % examples.length);

  return (
    <div className="g7eng-challenge-container">
      <h4>Advanced Challenge: {examples[index].name}</h4>
      <p className="g7eng-challenge-description">{examples[index].description}</p>
      <p className="g7eng-challenge-example">
        <strong>Tip:</strong> {examples[index].tip}
      </p>
      <button className="g7eng-next-challenge-btn" onClick={next}>
        See Another Project
      </button>
    </div>
  );
}

/** The main Grade7EngineeringLesson component */
export default function Grade7EngineeringLesson() {
  const [selectedTopic, setSelectedTopic] = useState(null);

  /** LESSON CONTENT for Grade 7 */
  const lessonContent = {
    structuralDesign: {
      title: "Structural Design",
      description: "Learn how engineers design strong, stable structures!",
      example: "Example: Bridges, skyscrapers, and towers under wind or load forces!",
      steps: [
        "Engineers analyze forces: tension, compression, torsion, shear.",
        "They use materials like steel, concrete, or composites for safety.",
        "Building codes ensure structures can handle earthquakes or storms.",
        "Advanced math and simulations help predict performance!",
        <Grade7Engineering3DDemo key="g7-demo" />,
      ],
    },
    renewableEnergy: {
      title: "Renewable Energy Systems",
      description: "Explore how engineers harness energy from natural sources!",
      example: "Example: Wind turbines, solar panels, or hydroelectric dams!",
      steps: [
        "Renewable sources (sun, wind, water) are cleaner and endless.",
        "Engineers design efficient turbines, panels, and storage solutions.",
        "Cost, location, and environmental impact shape design choices.",
        "Large-scale projects often involve power grids and distribution!",
        <Grade7AdvancedChallenge key="g7-challenge" />,
      ],
    },
  };

  /** QUIZ POOLS => 25 each => total 50 */
  // structuralDesignPool
  const structuralDesignPool = [
    {
      question: "Which force pulls structure parts apart?",
      correctAnswer: "Tension",
      options: ["Compression", "Tension", "Shear"],
    },
    {
      question: "Engineers use cross-bracing in tall buildings to?",
      correctAnswer: "Reduce sway and handle wind loads",
      options: [
        "Decorate the building",
        "Make walls see-through",
        "Reduce sway and handle wind loads",
      ],
    },
    // ... fill up to 25
    {
      question: "Which is a common material for structural frames?",
      correctAnswer: "Steel",
      options: ["Rubber", "Steel", "Paper only"],
    },
  ];

  // renewableEnergyPool
  const renewableEnergyPool = [
    {
      question: "Renewable energy sources are those that?",
      correctAnswer: "Can replenish naturally over time",
      options: [
        "Never exist in nature",
        "Can replenish naturally over time",
        "Only come from fossil fuels",
      ],
    },
    {
      question: "Solar panels convert?",
      correctAnswer: "Sunlight into electricity",
      options: [
        "Sound into heat",
        "Sunlight into electricity",
        "Water into hydrogen automatically",
      ],
    },
    // ... fill up to 25
    {
      question: "Which device captures kinetic energy from wind?",
      correctAnswer: "Wind turbine",
      options: ["Solar cell", "Wind turbine", "Water pipe"],
    },
  ];

  /** QUIZ States */
  const [quiz, setQuiz] = useState(null);
  const [selectedAnswer, setSelectedAnswer] = useState(null);
  const [feedback, setFeedback] = useState(null);

  const [finalQuizQuestions, setFinalQuizQuestions] = useState([]);
  const [finalQuizActive, setFinalQuizActive] = useState(false);
  const [finalQuizIndex, setFinalQuizIndex] = useState(0);
  const [finalQuizScore, setFinalQuizScore] = useState(0);
  const [timeLeft, setTimeLeft] = useState(300);

  // Timer for final quiz
  useEffect(() => {
    if (finalQuizActive && timeLeft > 0) {
      const timer = setTimeout(() => setTimeLeft((prev) => prev - 1), 1000);
      return () => clearTimeout(timer);
    } else if (timeLeft === 0) {
      setFinalQuizActive(false);
    }
  }, [finalQuizActive, timeLeft]);

  /** Generate Quick Quiz */
  const generateQuiz = (topic) => {
    const pool = topic === "structuralDesign" ? structuralDesignPool : renewableEnergyPool;
    const item = pool[Math.floor(Math.random() * pool.length)];
    setQuiz({
      question: item.question,
      correctAnswer: item.correctAnswer,
      options: shuffleArray([...item.options]),
    });
    setSelectedAnswer(null);
    setFeedback(null);
  };

  /** Quick Quiz answer */
  const handleAnswerClick = (answer) => {
    setSelectedAnswer(answer);
    if (quiz && answer === quiz.correctAnswer) {
      setFeedback("✅ Correct!");
    } else {
      setFeedback("❌ Try Again!");
    }
    setTimeout(() => {
      if (selectedTopic) generateQuiz(selectedTopic);
    }, 1500);
  };

  /** Final Quiz => 50 total Qs */
  const startFinalQuiz = () => {
    const combined = [...structuralDesignPool, ...renewableEnergyPool];
    const finalSet = shuffleArray(combined);
    setFinalQuizQuestions(finalSet);
    setFinalQuizActive(true);
    setFinalQuizIndex(0);
    setFinalQuizScore(0);
    setTimeLeft(300);
  };

  const handleFinalQuizAnswer = (choice) => {
    if (finalQuizQuestions[finalQuizIndex] && choice === finalQuizQuestions[finalQuizIndex].correctAnswer) {
      setFinalQuizScore((prev) => prev + 1);
    }
    if (finalQuizIndex < finalQuizQuestions.length - 1) {
      setFinalQuizIndex((prev) => prev + 1);
    } else {
      setFinalQuizActive(false);
    }
  };

  return (
    <div className="g7eng-page">
      <h1 className="g7eng-title">Grade 7 Engineering Lessons</h1>
      <p className="g7eng-description">Select a topic to learn more!</p>

      <div className="g7eng-topics">
        <button
          className="g7eng-topic-card"
          onClick={() => {
            setSelectedTopic("structuralDesign");
            generateQuiz("structuralDesign");
          }}
        >
          Structural Design
        </button>
        <button
          className="g7eng-topic-card"
          onClick={() => {
            setSelectedTopic("renewableEnergy");
            generateQuiz("renewableEnergy");
          }}
        >
          Renewable Energy
        </button>
      </div>

      {selectedTopic && (
        <div className="g7eng-lesson-quiz-container">
          {/* Left: Lesson Content */}
          <div className="g7eng-lesson-content">
            <h2 className="g7eng-lesson-title">
              {lessonContent[selectedTopic].title}
            </h2>
            <p className="g7eng-lesson-description">
              {lessonContent[selectedTopic].description}
            </p>
            <p className="g7eng-lesson-example">
              <strong>{lessonContent[selectedTopic].example}</strong>
            </p>
            <ul className="g7eng-lesson-steps">
              {lessonContent[selectedTopic].steps.map((step, idx) => (
                <li key={idx}>{step}</li>
              ))}
            </ul>
          </div>

          {/* Right: Quick Quiz + Final Quiz side by side */}
          <div className="g7eng-quiz-double-container">
            {/* Quick Quiz */}
            <div className="g7eng-quick-quiz-container">
              <h2 className="g7eng-quiz-title">Quick Quiz!</h2>
              {quiz && (
                <>
                  <p className="g7eng-quiz-question">{quiz.question}</p>
                  <div className="g7eng-quiz-options">
                    {quiz.options.map((opt, i) => (
                      <button
                        key={i}
                        className={`g7eng-quiz-btn ${selectedAnswer === opt ? "selected" : ""}`}
                        onClick={() => handleAnswerClick(opt)}
                      >
                        {opt}
                      </button>
                    ))}
                  </div>
                  {feedback && <p className="g7eng-quiz-feedback">{feedback}</p>}
                </>
              )}
            </div>

            {/* Final Quiz */}
            <div className="g7eng-final-quiz-container">
              <h2 className="g7eng-quiz-title">Final Quiz</h2>
              {!finalQuizActive && (
                <button className="g7eng-final-quiz-button" onClick={startFinalQuiz}>
                  Start Final Quiz (50 Questions)
                </button>
              )}
              {finalQuizActive && (
                <div className="g7eng-final-quiz-content">
                  <p>
                    Time Left: {Math.floor(timeLeft / 60)}:
                    {String(timeLeft % 60).padStart(2, "0")}
                  </p>
                  <p>
                    Question {finalQuizIndex + 1} of {finalQuizQuestions.length}
                  </p>
                  <p className="g7eng-quiz-question">
                    {finalQuizQuestions[finalQuizIndex].question}
                  </p>
                  <div className="g7eng-quiz-options">
                    {finalQuizQuestions[finalQuizIndex].options.map((choice, idx) => (
                      <button key={idx} onClick={() => handleFinalQuizAnswer(choice)}>
                        {choice}
                      </button>
                    ))}
                  </div>
                  <p>Score: {finalQuizScore}</p>
                  {finalQuizIndex === finalQuizQuestions.length - 1 && (
                    <p>
                      Final Score: {finalQuizScore}/{finalQuizQuestions.length}
                    </p>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      <Link to="/engineering" className="g7eng-back-button">
        &larr; Back to Engineering Page
      </Link>
    </div>
  );
}
